import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  MessageCircle, Send, ArrowLeft, Search, MoreVertical, 
  Ban, BellOff, Flag, Trash2, Image as ImageIcon, Link2,
  Loader2, X, Check, CheckCheck, AlertCircle
} from 'lucide-react';
import api from '../utils/api';
import Header from '../components/Header';
import { isHeicFile, convertHeicToJpeg, ACCEPTED_IMAGE_FORMATS } from '../utils/imageUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hook to get current user
const useCurrentUser = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await api.get('/auth/me');
        setUser(res.data);
      } catch {
        setUser(null);
      }
      setLoading(false);
    };
    fetchUser();
  }, []);

  return { user, loading };
};

// Conversation List Item
const ConversationItem = ({ conversation, isActive, onClick }) => {
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 86400000) { // Less than 24 hours
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diff < 604800000) { // Less than 7 days
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  return (
    <div 
      onClick={onClick}
      className={`p-4 flex items-center gap-3 cursor-pointer transition hover:bg-gray-50 ${
        isActive ? 'bg-emerald-50 border-l-4 border-emerald-500' : ''
      }`}
      data-testid={`conversation-${conversation.conversation_id}`}
    >
      <div className="relative">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-semibold">
          {conversation.other_user?.profile_image ? (
            <img 
              src={conversation.other_user.profile_image} 
              alt="" 
              className="w-12 h-12 rounded-full object-cover" 
            />
          ) : (
            conversation.other_user?.name?.charAt(0)?.toUpperCase() || '?'
          )}
        </div>
        {conversation.unread_count > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {conversation.unread_count > 9 ? '9+' : conversation.unread_count}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <p className="font-medium text-gray-900 truncate">
            {conversation.other_user?.name || 'User'}
          </p>
          <span className="text-xs text-gray-400">
            {formatTime(conversation.last_message_at)}
          </span>
        </div>
        <p className={`text-sm truncate ${
          conversation.unread_count > 0 ? 'text-gray-900 font-medium' : 'text-gray-500'
        }`}>
          {conversation.last_message || 'No messages yet'}
        </p>
      </div>
      {conversation.is_muted && (
        <BellOff className="h-4 w-4 text-gray-400" />
      )}
    </div>
  );
};

// Single Message Component
const Message = ({ message, isOwn, showAvatar, onBundleResponse, onOfferResponse }) => {
  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  const isBundleOffer = message.message_type === 'bundle_offer' && message.bundle_offer_id;
  const isItemOffer = message.message_type === 'offer' && message.message_id;
  const status = message.offer_status || 'pending';

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className={`flex items-end gap-2 max-w-[70%] ${isOwn ? 'flex-row-reverse' : ''}`}>
        {!isOwn && showAvatar && (
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-400 to-gray-500 flex items-center justify-center text-white text-xs flex-shrink-0">
            {message.sender_image ? (
              <img src={message.sender_image} alt="" className="w-8 h-8 rounded-full object-cover" />
            ) : (
              message.sender_name?.charAt(0)?.toUpperCase() || '?'
            )}
          </div>
        )}
        {!isOwn && !showAvatar && <div className="w-8" />}
        
        <div className={`rounded-2xl px-4 py-2 ${
          isOwn 
            ? 'bg-emerald-500 text-white rounded-br-sm' 
            : isBundleOffer ? 'bg-purple-50 text-gray-900 border border-purple-200 rounded-bl-sm'
            : isItemOffer ? 'bg-amber-50 text-gray-900 border border-amber-200 rounded-bl-sm'
            : 'bg-gray-100 text-gray-900 rounded-bl-sm'
        }`}>
          {/* Bundle offer header */}
          {isBundleOffer && (
            <div className={`flex items-center gap-1 mb-2 text-xs font-semibold ${isOwn ? 'text-emerald-100' : 'text-purple-700'}`}>
              <span>🎁 Bundle Offer</span>
              <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] ${
                status === 'pending' ? (isOwn ? 'bg-emerald-700' : 'bg-amber-100 text-amber-700') :
                status === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
                status === 'declined' ? 'bg-red-100 text-red-700' :
                'bg-blue-100 text-blue-700'
              }`}>
                {status.toUpperCase()}
              </span>
            </div>
          )}
          {/* Item offer header */}
          {isItemOffer && (
            <div className={`flex items-center gap-1 mb-2 text-xs font-semibold ${isOwn ? 'text-emerald-100' : 'text-amber-700'}`}>
              <span>💰 Offer · ${Number(message.offer_amount || 0).toFixed(2)}</span>
              <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] ${
                status === 'pending' ? (isOwn ? 'bg-emerald-700' : 'bg-amber-100 text-amber-700') :
                status === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
                status === 'declined' ? 'bg-red-100 text-red-700' :
                'bg-blue-100 text-blue-700'
              }`}>
                {status.toUpperCase()}
              </span>
            </div>
          )}
          {/* Text content */}
          {message.content && (
            <p className="whitespace-pre-wrap break-words">{message.content}</p>
          )}
          
          {/* Bundle Accept/Decline (only seller side, only when pending) */}
          {isBundleOffer && !isOwn && status === 'pending' && onBundleResponse && (
            <div className="flex gap-2 mt-3 pt-3 border-t border-purple-200">
              <button
                onClick={() => onBundleResponse(message.bundle_offer_id, 'accept')}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium px-3 py-1.5 rounded-md transition"
                data-testid={`bundle-accept-${message.bundle_offer_id}`}
              >
                Accept
              </button>
              <button
                onClick={() => onBundleResponse(message.bundle_offer_id, 'decline')}
                className="flex-1 bg-white hover:bg-red-50 text-red-600 border border-red-300 text-sm font-medium px-3 py-1.5 rounded-md transition"
                data-testid={`bundle-decline-${message.bundle_offer_id}`}
              >
                Decline
              </button>
              <button
                onClick={() => {
                  const counter = prompt('Counter offer amount:', message.offer_amount);
                  if (counter && !isNaN(parseFloat(counter))) {
                    onBundleResponse(message.bundle_offer_id, 'counter', parseFloat(counter));
                  }
                }}
                className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 text-sm font-medium px-3 py-1.5 rounded-md transition"
              >
                Counter
              </button>
            </div>
          )}

          {/* Item Offer Accept/Decline/Counter (only seller side, only when pending) */}
          {isItemOffer && !isOwn && status === 'pending' && onOfferResponse && (
            <div className="flex gap-2 mt-3 pt-3 border-t border-amber-200">
              <button
                onClick={() => onOfferResponse(message.message_id, 'accepted')}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium px-3 py-1.5 rounded-md transition"
                data-testid={`offer-accept-${message.message_id}`}
              >
                Accept
              </button>
              <button
                onClick={() => onOfferResponse(message.message_id, 'declined')}
                className="flex-1 bg-white hover:bg-red-50 text-red-600 border border-red-300 text-sm font-medium px-3 py-1.5 rounded-md transition"
                data-testid={`offer-decline-${message.message_id}`}
              >
                Decline
              </button>
              <button
                onClick={() => {
                  const counter = prompt('Counter offer amount:', message.offer_amount);
                  if (counter && !isNaN(parseFloat(counter)) && parseFloat(counter) > 0) {
                    onOfferResponse(message.message_id, 'counter', parseFloat(counter));
                  }
                }}
                className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 text-sm font-medium px-3 py-1.5 rounded-md transition"
                data-testid={`offer-counter-${message.message_id}`}
              >
                Counter
              </button>
            </div>
          )}

          {/* Pay Now (buyer side, when their offer was accepted) */}
          {isItemOffer && isOwn && status === 'accepted' && message.listing_id && (
            <div className="mt-3 pt-3 border-t border-emerald-300">
              <a
                href={`/item/${message.listing_id}?offer=${message.message_id}`}
                className="block w-full text-center bg-white hover:bg-emerald-50 text-emerald-700 font-semibold text-sm px-3 py-2 rounded-md border border-emerald-200 transition"
                data-testid={`pay-offer-${message.message_id}`}
              >
                Pay ${Number(message.offer_amount || 0).toFixed(2)} now →
              </a>
              <p className="text-[10px] text-emerald-50 mt-1 text-center">Offer accepted — complete payment to ship.</p>
            </div>
          )}
          {isItemOffer && isOwn && status === 'paid' && (
            <div className="mt-2 text-[10px] text-emerald-50 text-center">✓ Paid — seller will ship soon.</div>
          )}
          
          {/* Images */}
          {message.media_urls?.length > 0 && (
            <div className={`flex flex-wrap gap-1 mt-2 ${message.content ? '' : '-my-1'}`}>
              {message.media_urls.map((url, idx) => (
                <img 
                  key={idx} 
                  src={url} 
                  alt="" 
                  className="max-w-[200px] rounded-lg cursor-pointer hover:opacity-90"
                  onClick={() => window.open(url, '_blank')}
                />
              ))}
            </div>
          )}
          
          {/* Attached listing */}
          {message.listing && (
            <a 
              href={`/item/${message.listing.listing_id}`}
              className={`block mt-2 p-2 rounded-lg ${isOwn ? 'bg-emerald-600' : 'bg-white border'}`}
            >
              <div className="flex items-center gap-2">
                {message.listing.images?.[0] && (
                  <img src={message.listing.images[0]} alt="" className="w-12 h-12 rounded object-cover" />
                )}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${isOwn ? 'text-white' : 'text-gray-900'}`}>
                    {message.listing.title}
                  </p>
                  <p className={`text-sm ${isOwn ? 'text-emerald-100' : 'text-emerald-600'}`}>
                    ${message.listing.price?.toLocaleString()}
                  </p>
                </div>
                <Link2 className={`h-4 w-4 ${isOwn ? 'text-emerald-200' : 'text-gray-400'}`} />
              </div>
            </a>
          )}
          
          {/* Time and read status */}
          <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : ''}`}>
            <span className={`text-xs ${isOwn ? 'text-emerald-100' : 'text-gray-400'}`}>
              {formatTime(message.created_at)}
            </span>
            {isOwn && (
              message.read ? (
                <CheckCheck className="h-3 w-3 text-emerald-200" />
              ) : (
                <Check className="h-3 w-3 text-emerald-200" />
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Messages Page
const MessagesPage = () => {
  const { user, loading: userLoading } = useCurrentUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeConvId = searchParams.get('conversation');
  
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [error, setError] = useState('');
  const [mediaUrls, setMediaUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    if (!user) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setConversations(data.conversations || []);
    } catch (e) {
      console.error('Error fetching conversations:', e);
    }
    setLoading(false);
  }, [user]);

  // Fetch messages for active conversation
  const fetchMessages = useCallback(async (convId) => {
    if (!convId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/messages/conversations/${convId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setMessages(data.messages || []);
      setActiveConversation({
        conversation_id: convId,
        other_user: data.other_user,
        is_blocked: data.is_blocked,
        blocked_by: data.blocked_by
      });
      
      // Update conversation unread count
      setConversations(prev => prev.map(c => 
        c.conversation_id === convId ? { ...c, unread_count: 0 } : c
      ));
    } catch (e) {
      console.error('Error fetching messages:', e);
    }
  }, []);

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!user) return;

    const token = localStorage.getItem('token');
    const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/messages/ws/${user.user_id}?token=${token}`);
    
    ws.onopen = () => {
      console.log('DM WebSocket connected');
      // Send ping every 30 seconds
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000);
      ws.pingInterval = pingInterval;
    };
    
    ws.onmessage = (event) => {
      if (event.data === 'pong') return;
      
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'new_message' || data.type === 'message_sent') {
          // Add message if viewing that conversation
          if (activeConvId === data.conversation_id) {
            setMessages(prev => {
              // Check if message already exists
              if (prev.some(m => m.message_id === data.message.message_id)) {
                return prev;
              }
              return [...prev, data.message];
            });
          }
          
          // Update conversation list
          fetchConversations();
        }
      } catch (e) {
        console.error('WS message error:', e);
      }
    };
    
    ws.onerror = (error) => {
      console.error('DM WebSocket error:', error);
    };
    
    ws.onclose = () => {
      console.log('DM WebSocket disconnected');
      if (ws.pingInterval) clearInterval(ws.pingInterval);
    };
    
    wsRef.current = ws;
    
    return () => {
      if (ws.pingInterval) clearInterval(ws.pingInterval);
      ws.close();
    };
  }, [user, activeConvId, fetchConversations]);

  // Initial load
  useEffect(() => {
    if (user) {
      fetchConversations();
    }
  }, [user, fetchConversations]);

  // Load active conversation
  useEffect(() => {
    if (activeConvId && user) {
      fetchMessages(activeConvId);
    } else {
      setActiveConversation(null);
      setMessages([]);
    }
  }, [activeConvId, user, fetchMessages]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Send message
  const handleSend = async () => {
    if ((!newMessage.trim() && mediaUrls.length === 0) || !activeConversation) return;
    
    setSending(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/messages/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          recipient_id: activeConversation.other_user.user_id,
          content: newMessage.trim(),
          media_urls: mediaUrls,
          listing_id: null
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setNewMessage('');
        setMediaUrls([]);
        // Message will be added via WebSocket
      } else {
        setError(data.detail || 'Failed to send message');
      }
    } catch (e) {
      setError('Failed to send message');
    }
    
    setSending(false);
    inputRef.current?.focus();
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (mediaUrls.length + files.length > 5) {
      setError('Maximum 5 images per message');
      return;
    }

    setUploading(true);
    for (const file of files) {
      try {
        // Convert HEIC to JPEG if needed
        let processedFile = file;
        if (isHeicFile(file)) {
          try {
            processedFile = await convertHeicToJpeg(file);
          } catch (convError) {
            console.error('HEIC conversion error:', convError);
            setError('Failed to convert HEIC image. Please try a different format.');
            continue;
          }
        }
        
        const formData = new FormData();
        formData.append('file', processedFile);
        const res = await api.post('/upload', formData);
        setMediaUrls(prev => [...prev, res.data.url]);
      } catch (e) {
        console.error('Upload error:', e);
      }
    }
    setUploading(false);
  };

  // Block/Unblock user
  const handleBlock = async () => {
    if (!activeConversation) return;
    
    const action = activeConversation.is_blocked ? 'unblock' : 'block';
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/messages/${action}/${activeConversation.other_user.user_id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setActiveConversation(prev => ({
          ...prev,
          is_blocked: !prev.is_blocked
        }));
        setShowMenu(false);
      }
    } catch (e) {
      console.error('Block error:', e);
    }
  };

  // Mute conversation
  const handleMute = async () => {
    if (!activeConversation) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/messages/mute/${activeConversation.conversation_id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setConversations(prev => prev.map(c => 
          c.conversation_id === activeConversation.conversation_id 
            ? { ...c, is_muted: data.muted }
            : c
        ));
        setShowMenu(false);
      }
    } catch (e) {
      console.error('Mute error:', e);
    }
  };

  // Respond to a bundle offer (accept / decline / counter)
  const handleBundleResponse = async (bundleOfferId, action, counterPrice = null) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/cart/bundle-offers/${bundleOfferId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action, counter_price: counterPrice })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to respond to offer');
        return;
      }
      const labelMap = { accept: 'accepted', decline: 'declined', counter: 'countered' };
      // Optimistic UI update on the message
      setMessages(prev => prev.map(m =>
        m.bundle_offer_id === bundleOfferId
          ? { ...m, offer_status: labelMap[action] || action }
          : m
      ));
    } catch (e) {
      console.error('Bundle response error:', e);
      alert('Failed to respond to offer');
    }
  };

  // Respond to a single-item offer (accept / decline / counter)
  const handleOfferResponse = async (messageId, status, counterAmount = null) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/messages/offer/${messageId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status, counter_amount: counterAmount })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to respond to offer');
        return;
      }
      // Optimistic UI update on the offer message
      setMessages(prev => prev.map(m =>
        m.message_id === messageId
          ? { ...m, offer_status: status }
          : m
      ));
      // Refresh messages to fetch the auto-generated response message
      if (activeConversation) {
        try {
          const mres = await fetch(`${API_URL}/api/messages/conversations/${activeConversation.conversation_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (mres.ok) {
            const data = await mres.json();
            setMessages(data.messages || data || []);
          }
        } catch (_) {}
      }
    } catch (e) {
      console.error('Offer response error:', e);
      alert('Failed to respond to offer');
    }
  };

  // Redirect to auth if not logged in
  if (!userLoading && !user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header user={user} />
        <div className="h-[184px]"></div>
        <div className="flex items-center justify-center h-[calc(100vh-264px)]">
          <div className="text-center">
            <MessageCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Sign in to view messages</h2>
            <button
              onClick={() => navigate('/auth')}
              className="px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600"
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentConv = conversations.find(c => c.conversation_id === activeConvId);

  return (
    <div className="min-h-screen bg-gray-100">
      <Header user={user} />
      
      {/* Spacer for fixed header */}
      <div className="h-[184px]"></div>
      
      <div className="max-w-6xl mx-auto h-[calc(100vh-184px)] flex">
        {/* Conversations List */}
        <div className={`w-full md:w-80 bg-white border-r flex flex-col ${activeConvId ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-4 border-b">
            <h1 className="text-xl font-bold text-gray-900">Messages</h1>
          </div>
          
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex-1 flex items-center justify-center p-4 text-center">
              <div>
                <MessageCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No conversations yet</p>
                <p className="text-sm text-gray-400 mt-1">
                  Start a conversation from a user's profile or listing
                </p>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto divide-y">
              {conversations.map((conv) => (
                <ConversationItem
                  key={conv.conversation_id}
                  conversation={conv}
                  isActive={conv.conversation_id === activeConvId}
                  onClick={() => navigate(`/messages?conversation=${conv.conversation_id}`)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Chat Area */}
        <div className={`flex-1 flex flex-col bg-white ${activeConvId ? 'flex' : 'hidden md:flex'}`}>
          {activeConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate('/messages')}
                    className="md:hidden p-2 hover:bg-gray-100 rounded-full"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-semibold">
                    {activeConversation.other_user?.profile_image ? (
                      <img 
                        src={activeConversation.other_user.profile_image} 
                        alt="" 
                        className="w-10 h-10 rounded-full object-cover" 
                      />
                    ) : (
                      activeConversation.other_user?.first_name?.charAt(0)?.toUpperCase() || '?'
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {activeConversation.other_user?.first_name} {activeConversation.other_user?.last_name}
                    </p>
                    {activeConversation.blocked_by && (
                      <p className="text-xs text-red-500">This user has blocked you</p>
                    )}
                  </div>
                </div>
                
                <div className="relative">
                  <button
                    onClick={() => setShowMenu(!showMenu)}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <MoreVertical className="h-5 w-5 text-gray-500" />
                  </button>
                  
                  {showMenu && (
                    <div className="absolute right-0 top-12 bg-white shadow-lg rounded-lg border py-1 z-10 min-w-[160px]">
                      <button
                        onClick={handleMute}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                      >
                        <BellOff className="h-4 w-4" />
                        {currentConv?.is_muted ? 'Unmute' : 'Mute'}
                      </button>
                      <button
                        onClick={handleBlock}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600"
                      >
                        <Ban className="h-4 w-4" />
                        {activeConversation.is_blocked ? 'Unblock' : 'Block User'}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                {messages.length === 0 ? (
                  <div className="text-center text-gray-400 py-8">
                    <p>No messages yet</p>
                    <p className="text-sm">Send a message to start the conversation</p>
                  </div>
                ) : (
                  messages.map((msg, idx) => {
                    const isOwn = msg.sender_id === user?.user_id;
                    const prevMsg = messages[idx - 1];
                    const showAvatar = !isOwn && (!prevMsg || prevMsg.sender_id !== msg.sender_id);
                    
                    return (
                      <Message 
                        key={msg.message_id} 
                        message={msg} 
                        isOwn={isOwn}
                        showAvatar={showAvatar}
                        onBundleResponse={handleBundleResponse}
                        onOfferResponse={handleOfferResponse}
                      />
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              {activeConversation.blocked_by ? (
                <div className="p-4 border-t bg-gray-50 text-center text-gray-500">
                  You cannot reply to this conversation
                </div>
              ) : activeConversation.is_blocked ? (
                <div className="p-4 border-t bg-gray-50 text-center">
                  <p className="text-gray-500 mb-2">You have blocked this user</p>
                  <button
                    onClick={handleBlock}
                    className="text-emerald-600 text-sm hover:underline"
                  >
                    Unblock to send messages
                  </button>
                </div>
              ) : (
                <div className="p-4 border-t">
                  {error && (
                    <div className="mb-2 p-2 bg-red-50 text-red-600 text-sm rounded-lg flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      {error}
                    </div>
                  )}
                  
                  {/* Image previews */}
                  {mediaUrls.length > 0 && (
                    <div className="flex gap-2 mb-2 flex-wrap">
                      {mediaUrls.map((url, idx) => (
                        <div key={idx} className="relative">
                          <img src={url} alt="" className="h-16 w-16 object-cover rounded-lg" />
                          <button
                            onClick={() => setMediaUrls(prev => prev.filter((_, i) => i !== idx))}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex items-end gap-2">
                    <label className="p-2 hover:bg-gray-100 rounded-full cursor-pointer">
                      <ImageIcon className="h-5 w-5 text-gray-500" />
                      <input
                        type="file"
                        accept={ACCEPTED_IMAGE_FORMATS}
                        multiple
                        className="hidden"
                        onChange={handleImageUpload}
                        disabled={uploading}
                      />
                    </label>
                    
                    <div className="flex-1 relative">
                      <textarea
                        ref={inputRef}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSend();
                          }
                        }}
                        placeholder="Type a message..."
                        className="w-full px-4 py-2.5 border rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent max-h-32"
                        rows={1}
                        data-testid="message-input"
                      />
                    </div>
                    
                    <button
                      onClick={handleSend}
                      disabled={sending || (!newMessage.trim() && mediaUrls.length === 0)}
                      className="p-3 bg-emerald-500 text-white rounded-full hover:bg-emerald-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
                      data-testid="send-message-btn"
                    >
                      {sending ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <Send className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-center p-4">
              <div>
                <MessageCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">Select a conversation</p>
                <p className="text-gray-400 text-sm mt-1">
                  Choose from your existing conversations or start a new one
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
